// The specification row of the dock (R1813, amended by R1847; mockup round
// 085 card 01 and round 087 card 04 B, with the owner's amendments of
// 2026-10-09; ui-map-interface.md §8 "Specification rows"). Every list of
// the dock that buys a thing draws its rows here: BUILD · BASE, TECH,
// SHIPYARD and TORPEDO, the auto-rebuild switch, and the library of
// DRAFT · PRINTS and DRAFT · MASTERS (R1888, `library.ts`). ECONOMY and WAR
// take the same row for the stores, the auction and the strategic objects
// (round 091 card 09, R1892): the figure column (`side`) in place of the
// cost column and a kind word in the cap step after the name.
//
// A row is 44 px high and has four columns: the icon; the name over line 2
// (the condensed effect "key now → after" of the next level); a building
// row states its level after the name on line 1, "Hold 5 +1", R1874); the cost
// column, the cost over the time;
// and the press, one narrow width on every row, with the verb alone. The
// press never states a time or a cost: it stays the press of the next
// queue action, and the queue stays in the queue strip. A state reads by
// shape: LOCKED has a dashed edge and the reason on line 2, a row short of
// stores has a faint press and the short figure in the bad ink in the cost
// column, and MAX and DONE are a solid word.

import type { Resources } from '../../gen/proto/aquaphobia';
import { overlay as S, card as CS } from '../../gen/strings';
import { costText, dur } from '../../state';
import type { Action, ChipTone } from '../cards/card';
import { shortOf, shortWaitSec } from '../storewait';

/** The line icons of the build rows, in a 24-unit box, drawn with the
 *  stroke of the dock icons (R1740). */
const ICON = {
  // A station with a door: a building of the hold.
  building: '<path d="M4 20h16M6 20V10l6-5 6 5v10"/><path d="M10 20v-5h4v5"/>',
  // A chip with its pins: a tech node, as the Data Core draws it.
  tech: '<rect x="7" y="7" width="10" height="10" rx="1"/>'
    + '<path d="M10 3.5V7M14 3.5V7M10 17v3.5M14 17v3.5M3.5 10H7M3.5 14H7M17 10h3.5M17 14h3.5"/>',
  // A hull with its sail: a hull class.
  hull: '<path d="M3 14c0-2 2-3 5-3h8c3 0 5 1.5 5 3s-2 3-5 3H8c-3 0-5-1-5-3z"/><path d="M10 11V7.5h4V11"/>',
  // A round with its fins: a torpedo round.
  round: '<path d="M4 10.5h10.5c2 0 3.5.7 3.5 1.5s-1.5 1.5-3.5 1.5H4z"/><path d="M4 8.5v7M18 12h2.5"/>',
  // A horned ball: a sea mine of the Depot (R2653).
  mine: '<circle cx="12" cy="13.5" r="5"/><path d="M12 8.5V4.5M7.6 10.2L5 7.6M16.4 10.2L19 7.6"/>',
  // A buoy on its anchor line over the water: a deployable (R2653).
  deployable: '<circle cx="12" cy="7.5" r="3"/><path d="M12 10.5v6M9 16.5h6"/>'
    + '<path d="M3.5 20c2.8-1.4 5.7-1.4 8.5 0s5.7 1.4 8.5 0"/>',
  // A turning arrow: the auto-rebuild switch.
  auto: '<path d="M19 12a7 7 0 1 1-2.05-4.95"/><path d="M19 4.5V8h-3.5"/>',
  // A line of water over a descent: a dive (R1893).
  dive: '<path d="M3 6.5c2-1.5 4-1.5 6 0s4 1.5 6 0 4-1.5 6 0"/><path d="M12 10v10M8.5 16.5L12 20l3.5-3.5"/>',
  // A padlock: LOCKED.
  lock: '<rect x="6" y="11" width="12" height="9" rx="1.5"/><path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3"/>',
  // A check: a step of the tech ladder in hand (084 card 15).
  check: '<path d="M5 12.5l4.5 4.5L19 7.5"/>',
  // Two rank chevrons: a captain (084 card 16).
  captain: '<path d="M5 10.5l7-4.5 7 4.5M5 16l7-4.5 7 4.5"/>',
  // A family of three slots: a module family.
  module: '<rect x="4" y="7" width="4.5" height="10" rx="1"/><rect x="9.75" y="7" width="4.5" height="10" rx="1"/>'
    + '<rect x="15.5" y="7" width="4.5" height="10" rx="1"/>',
} as const;

/** The name of one icon of the build rows. */
type RowIcon = keyof typeof ICON;

/** One line icon of a build row, hidden from a screen reader. */
export function rowIcon(name: RowIcon): string {
  return `<svg class="bic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"`
    + ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">'
    + `${ICON[name]}</svg>`;
}

/**
 * The state of a press, which its shape states:
 *
 * - `ready`: the verb; the press acts. The cost column states the cost.
 * - `short`: a faint press; the cost column inks the short figure in the
 *   bad ink (R45).
 * - `lock`: a dashed press that reads LOCKED; line 2 names the gate, and
 *   the cost column states the cost in the faint ink.
 * - `max`: the solid word MAX with no icon (R1860); the building stands
 *   at its top level.
 * - `done`: the solid word DONE; the tech node is decrypted.
 * - `held`: a faint press with the verb alone; the job is in the queue,
 *   and the queue strip states its time.
 */
export type PressState = 'ready' | 'short' | 'lock' | 'max' | 'done' | 'held';

/** One press of a build row. */
export interface RowPress {
  act: string;
  id: number;
  /** The count the order carries, for a press that states one. */
  n?: number;
  /** The verb (BUILD, DECRYPT, BUILD ×4). */
  word: string;
  /** A word the host reads back off the press as `data-key`: the arcade
   *  build pane names its row with it (R1933). */
  key?: string;
  /** The cost of the next level or of the order. */
  cost?: Resources;
  /** The stores, which mark the short part of the cost. */
  have?: Resources;
  /** The rate of each store per game hour (`storeRates`), which turns the
   *  short part into a wait (R3258). Absent: the row states no wait. */
  rates?: Resources;
  state: PressState;
}

/** One build row. Every string is markup that is already escaped. */
export interface BuildRow {
  /** The markup of the icon: the item icon of `ui/buildicons.ts` for a
   *  building or a tech step (R1855), else a `rowIcon`. */
  icon: string;
  /** The name: the info button that opens the card, or a plain name. */
  name: string;
  /** The text of line 2: the condensed effect, the gate or the reason. */
  line2: string;
  /** The time of the next level or of the order. The cost column states
   *  it under the cost, except for a press at MAX, DONE or in the queue. */
  time?: string;
  /** The level that line 1 states after the name, or undefined for a row
   *  with no level: a tech node, a hull or a round (R1874). */
  level?: number;
  /** The levels already in the queue, which line 1 states as +N after the
   *  name. */
  queued?: number;
  /** The press, or the markup of a press that is no queue order (the
   *  auto-rebuild switch, the press and ⋯ of a library row of DRAFT). */
  press: RowPress | string;
  /** The markup of the cost column in place of the cost and the time of
   *  the press: a library row states the cost of one hull built to the
   *  design over its weight (R1888); a row of ECONOMY or WAR states its
   *  figure column, the stock over its cap, a price or a window clock
   *  (R1892); a dive row its duration over its risk (R1893). */
  side?: string;
  /** The markup of a third line under the name and the cost column, which
   *  runs on under the cost column up to the right edge: the typed systems
   *  of a library row (R1888). The row then grows to three lines. */
  line3?: string;
  /** More classes of the row: `printed`, `keystone`. */
  cls?: string;
  /** More classes of line 2. */
  subCls?: string;
  /** Line 2 is a reason or a sentence, which never cuts (R2061): it takes
   *  a second line and the row grows. A row whose press is LOCKED states
   *  its reason there and wraps without this flag. */
  wrap?: boolean;
  /** A word in the cap step after the name: the kind of an object, the
   *  runs of a print (R1892). Escaped markup. */
  kind?: string;
  /** More attributes of the row, as trusted markup that starts with a
   *  space: the arcade build pane makes the row body the press that opens
   *  the build card (R1933). */
  attrs?: string;
}

/** The word of a press: the verb, or the word of its state. */
function pressWord(p: RowPress): string {
  switch (p.state) {
    case 'lock': return S.btn_locked;
    case 'max': return S.btn_max;
    case 'done': return S.btn_done;
    default: return p.word;
  }
}

/** Does the press show a word of its state in place of its verb? */
const showsState = (st: PressState): boolean => st === 'lock' || st === 'max' || st === 'done';

/** The press of a build row: one narrow width on every row (`--w-press`).
 *  Only a `ready` press acts. A press that shows a word of its state keeps
 *  the room of its verb (`data-verb`, which the sheet draws hidden under
 *  the word), so a list whose verb is wider than the press, the German
 *  DECRYPT, keeps one press width on every row (R1860). */
export function pressHtml(p: RowPress): string {
  const n = (p.n !== undefined ? ` data-n="${p.n}"` : '') + (p.key !== undefined ? ` data-key="${p.key}"` : '');
  const off = p.state === 'ready' ? '' : ' disabled';
  const verb = showsState(p.state) ? ` data-verb="${p.word}"` : '';
  return `<button class="bp ${p.state}" data-act="${p.act}" data-id="${p.id}"${n}${verb}${off}><span class="w">${pressWord(p)}</span></button>`;
}

/** Does the cost column state the cost and the time? Only for a press
 *  that a player can press now, once the stores land, or once the gate
 *  opens. */
const showsCost = (st: PressState): boolean => st === 'ready' || st === 'short' || st === 'lock';

/** The game seconds until the rates close the shortfall of a press short
 *  of stores (R3258), or undefined: a press that is not short, or a short
 *  part with no rate (the isotopes). */
export function shortWait(p: RowPress): number | undefined {
  if (p.state !== 'short' || !p.cost || !p.have || !p.rates) return undefined;
  return shortWaitSec(p.cost, p.have, p.rates);
}

/** The sentence of a press short of stores for the note of its card
 *  (R3258): what the stores lack, in the colours and icons of a cost, and
 *  the wait while a rate closes it. Markup, or '' for a press that is not
 *  short. */
export function shortNote(p: RowPress): string {
  if (p.state !== 'short' || !p.cost || !p.have) return '';
  const missing = costText(shortOf(p.cost, p.have));
  const wait = shortWait(p);
  return wait === undefined ? S.short_needs({ missing }) : S.short_needs_wait({ missing, wait: dur(wait) });
}

/** The cost column (R1847, round 087 card 04 B): the cost over the time,
 *  right-aligned, of one minimum width on every row. A press short of
 *  stores inks the part the stores cannot pay in the bad ink, and its
 *  time slot states the wait until the rates close the shortfall
 *  (R3258); a part with no rate keeps the time of the order. */
function costCol(p: RowPress | string, time: string | undefined, side?: string): string {
  if (typeof p === 'string' || side !== undefined) return `<span class="costcol">${side ?? ''}</span>`;
  if (!showsCost(p.state)) return '<span class="costcol"></span>';
  const cost = p.cost
    ? `<span class="cost${p.state === 'lock' ? ' faint' : ''}">${costText(p.cost, p.state === 'short' ? p.have : undefined)}</span>`
    : '';
  const wait = shortWait(p);
  const slot = wait !== undefined ? `<span class="time">${S.row_short_in({ wait: dur(wait) })}</span>`
    : time ? `<span class="time">${time}</span>` : '';
  return `<span class="costcol">${cost}${slot}</span>`;
}

/**
 * The level mark of line 1 (R1874, which replaced the LVL pill of
 * R1863): the level with no padding, after the name and before the +N of
 * the queue, in the dim ink. The title
 * and the label for a screen reader say the level in words, with the
 * levels in the queue, because a bare number names nothing.
 */
function levelPrefix(level: number, queued: number): string {
  const label = queued ? S.row_lvl_queued_label({ level, n: queued }) : S.row_lvl_label({ level });
  return `<span class="lvpre" role="img" title="${label}" aria-label="${label}">${S.row_lvl({ level })}</span>`;
}

/** One build row (R1813, R1847, R1874, R1892). */
export function buildRowHtml(r: BuildRow): string {
  const pre = r.level !== undefined ? levelPrefix(r.level, r.queued ?? 0) : '';
  // The level and the levels in the queue stand after the name on line 1.
  const q = r.level !== undefined && r.queued ? `<span class="lvq">${S.row_queued({ n: r.queued })}</span>` : '';
  const p = r.press;
  // A refusal reason never cuts (R2061): a LOCKED row and a row that asks
  // for it take the class `rwrap`, and line 2 takes a second line.
  const wrap = r.wrap === true || (typeof p !== 'string' && p.state === 'lock');
  const cls = [r.cls ?? '', wrap ? 'rwrap' : ''].filter(Boolean).join(' ');
  const kind = r.kind ? `<small class="kind">${r.kind}</small>` : '';
  return `<div class="row spec${cls ? ` ${cls}` : ''}"${r.attrs ?? ''}>`
    + `<span class="ric">${r.icon}</span>`
    + `<span class="ident">${r.name}${kind}${pre}${q}</span>`
    + `<span class="sub${r.subCls ? ` ${r.subCls}` : ''}">`
    + (r.line2 ? `<span class="eff">${r.line2}</span>` : '') + '</span>'
    + (r.line3 ? `<span class="sub l3">${r.line3}</span>` : '')
    + costCol(p, r.time, r.side)
    + (typeof p === 'string' ? p : pressHtml(p))
    + '</div>';
}

/**
 * The verb press of a row as the press of its card (R1826, round 084 card
 * 13): the same act, id and count, the verb and the cost in the dark face.
 * A press that cannot act now stands off with the word of its state, so the
 * card and the row never offer two different things.
 */
export function pressAction(p: RowPress): Action {
  const word = pressWord(p);
  const priced = p.cost && (p.state === 'ready' || p.state === 'short');
  return {
    act: p.act, id: p.id, n: p.n, dataKey: p.key, face: 'dark', label: word, off: p.state !== 'ready',
    costHtml: priced ? costText(p.cost, p.state === 'short' ? p.have : undefined) : undefined,
  };
}

/** The status chip of a card that names the state of its verb press. */
export function pressChip(st: PressState): { text: string; tone: ChipTone } {
  switch (st) {
    case 'ready': return { text: CS.st_ready, tone: 'good' };
    case 'short': return { text: CS.st_short, tone: 'bad' };
    case 'lock': return { text: CS.st_locked, tone: 'kind' };
    case 'max': return { text: CS.st_max, tone: 'good' };
    case 'done': return { text: CS.st_done, tone: 'good' };
    case 'held': return { text: CS.st_queued, tone: 'on' };
  }
}
